/**
 * ч.1094: «Дублі контрагентів» — після імпорту кількох баз 1С той самий
 * контрагент буває двічі (різні назви, один код ЄДРПОУ/РНОКПП).
 *
 * Група — картки з однаковим кодом (або однаковою назвою без коду).
 * «Очевидні» дублі (той самий код і назва «про те саме», картки з різних баз)
 * мають рекомендовану основну картку й обʼєднуються однією кнопкою; решта —
 * на рішення людини (це можуть бути філії або окремі отримувачі з одним
 * кодом). Обʼєднання переносить усі документи, договори, проводки й
 * розрахунки дубля на основну картку в усіх організаціях і є незворотним —
 * тому доступне лише власнику та адміністратору акаунта.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { contractors, type DuplicateGroup } from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import { useToast } from '@/components/ToastProvider';
import { extractApiError } from '@/utils/extractApiError';
import TchTable from '@/shared/components/TchTable';

const muted = { color: 'var(--text-secondary)', fontSize: '0.85rem' } as const;

/** Вибір у групі: основна картка + позначені дублі. */
interface Choice { main: string; dups: Set<string> }

export function initialChoice(g: DuplicateGroup): Choice {
  const cl = g.obvious[0];
  if (cl) return { main: cl.main_id, dups: new Set(cl.duplicate_ids) };
  const top = [...g.members].sort((a, b) => b.docs_count - a.docs_count)[0];
  return { main: top?.id ?? '', dups: new Set() };
}

function GroupCard({ g, canMerge, onMerged }: { g: DuplicateGroup; canMerge: boolean; onMerged: () => void }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [choice, setChoice] = useState<Choice>(() => initialChoice(g));
  const merge = useMutation({
    mutationFn: () => contractors.merge(choice.main, [...choice.dups]),
    onSuccess: (r) => {
      const docs = Object.values(r.moved).reduce((a, b) => a + b, 0);
      toast.show({ type: 'success', message: `Обʼєднано карток: ${r.merged}; перенесено посилань: ${docs}.` });
      onMerged();
    },
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e, 'Не вдалося обʼєднати') }),
  });
  const mainName = g.members.find((m) => m.id === choice.main)?.name ?? '';
  const obvious = g.obvious.length > 0;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.6rem 0.8rem', marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <b>{g.kind === 'code' ? `Код ${g.key}` : `Назва «${g.key}» (без коду)`}</b>
        <span style={muted}>
          {obvious
            ? 'очевидний дубль — основну картку обрано, можна обʼєднувати'
            : g.kind === 'code'
              ? 'однаковий код, різні назви — перевірте: це може бути філія, окремий отримувач або помилка в коді'
              : 'однакова назва без коду — перевірте, чи це той самий контрагент'}
        </span>
      </div>
      <TchTable style={{ marginTop: '0.4rem' }}>
        <thead>
          <tr>
            <th style={{ width: 70 }}>основна</th>
            <th style={{ width: 70 }}>дубль</th>
            <th>Назва</th>
            <th>Повна назва</th>
            <th style={{ width: 80, textAlign: 'right' }}>Документів</th>
            <th>Організації, де використовується</th>
            <th style={{ width: 90 }}>Створено</th>
          </tr>
        </thead>
        <tbody>
          {g.members.map((m) => (
            <tr key={m.id}>
              <td style={{ textAlign: 'center' }}>
                <input type="radio" style={{ width: 'auto' }} name={`main-${g.key}`}
                  aria-label={`основна: ${m.name}`} checked={choice.main === m.id} disabled={!canMerge}
                  onChange={() => setChoice((c) => {
                    const dups = new Set(c.dups); dups.delete(m.id);
                    if (c.main && c.main !== m.id && obvious && m.cluster !== null) dups.add(c.main);
                    return { main: m.id, dups };
                  })} />
              </td>
              <td style={{ textAlign: 'center' }}>
                <input type="checkbox" style={{ width: 'auto' }} aria-label={`дубль: ${m.name}`}
                  checked={choice.dups.has(m.id)} disabled={!canMerge || choice.main === m.id}
                  onChange={(e) => setChoice((c) => {
                    const dups = new Set(c.dups);
                    if (e.target.checked) dups.add(m.id); else dups.delete(m.id);
                    return { ...c, dups };
                  })} />
              </td>
              <td>
                {m.external_source && (
                  <span title="Імпортовано з 1С" style={{ color: '#2563eb', border: '1px solid #bfdbfe',
                    borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, padding: '0 4px', marginRight: 6 }}>I</span>
                )}
                <Link to={`/contractors/${m.id}`}>{m.name}</Link>
              </td>
              <td>{m.full_name && m.full_name !== m.name ? m.full_name : ''}</td>
              <td style={{ textAlign: 'right' }}>{m.docs_count}</td>
              <td>{m.organizations.join(', ')}</td>
              <td>{new Date(m.created_at).toLocaleDateString('uk-UA')}</td>
            </tr>
          ))}
        </tbody>
      </TchTable>
      {canMerge && (
        <div className="sup__actions" style={{ alignItems: 'center' }}>
          <span style={{ ...muted, marginRight: 'auto' }}>
            {choice.dups.size > 0
              ? `Документи ${choice.dups.size} позначених карток перейдуть на «${mainName}».`
              : 'Позначте картки-дублі, які треба влити в основну.'}
          </span>
          <button type="button" className="primary" disabled={choice.dups.size === 0 || !choice.main || merge.isPending}
            onClick={async () => {
              if (await confirm({
                title: 'Обʼєднати контрагентів?',
                message: `Усі документи, договори, проводки і розрахунки позначених карток (${choice.dups.size}) `
                  + `перейдуть на «${mainName}» в усіх організаціях. Дублі зникнуть зі списку. Скасувати це не можна.`,
                okLabel: 'обʼєднати',
              })) merge.mutate();
            }}>
            {merge.isPending ? 'обʼєдную…' : 'обʼєднати позначені'}
          </button>
        </div>
      )}
    </div>
  );
}

export default function ContractorDuplicatesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const q = useQuery({ queryKey: ['contractor-duplicates'], queryFn: contractors.duplicates });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['contractor-duplicates'] });
    qc.invalidateQueries({ queryKey: ['contractors-full'] });
  };
  const mergeAll = useMutation({
    mutationFn: contractors.mergeObvious,
    onSuccess: (r) => {
      toast.show({
        type: r.errors.length ? 'warning' : 'success',
        message: `Обʼєднано карток: ${r.merged}.` + (r.errors.length ? ` Не вдалося: ${r.errors.length} (${r.errors[0]}).` : ''),
      });
      refresh();
    },
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e, 'Не вдалося обʼєднати') }),
  });
  const data = q.data;
  const groups = data?.groups ?? [];
  const { obviousGroups, reviewGroups } = useMemo(() => ({
    obviousGroups: groups.filter((g) => g.obvious.length > 0),
    reviewGroups: groups.filter((g) => g.obvious.length === 0),
  }), [groups]);

  return (
    <div className="card">
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0 }}>Дублі контрагентів</h2>
        <span style={{ marginLeft: 'auto' }}><Link to="/contractors">← до контрагентів</Link></span>
      </div>
      <p style={{ ...muted, marginTop: 0 }}>
        Той самий контрагент міг потрапити в довідник двічі — наприклад, з двох баз 1С з різним написанням назви.
        Обʼєднання переносить усі документи, договори, проводки і розрахунки дубля на основну картку в усіх
        організаціях (суми й рахунки не змінюються, закриті періоди не перераховуються) і є незворотним.
        {data && !data.can_merge && ' Обʼєднувати може лише власник або адміністратор акаунта.'}
      </p>
      {q.isLoading && <div style={muted}>Шукаю дублі…</div>}
      {data && groups.length === 0 && <div style={muted}>Дублів не знайдено.</div>}

      {obviousGroups.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.5rem 0' }}>
            <h3 style={{ margin: 0 }}>Очевидні дублі ({obviousGroups.length})</h3>
            {data?.can_merge && (
              <button type="button" className="primary" disabled={mergeAll.isPending}
                onClick={async () => {
                  if (await confirm({
                    title: 'Обʼєднати всі очевидні дублі?',
                    message: `Буде обʼєднано карток: ${data.obvious_merges} (однаковий код і назва «про те саме»). `
                      + 'Основна картка в кожній групі — та, де більше документів. Скасувати це не можна.',
                    okLabel: 'обʼєднати всі',
                  })) mergeAll.mutate();
                }}>
                {mergeAll.isPending ? 'обʼєдную…' : `обʼєднати всі очевидні (${data.obvious_merges})`}
              </button>
            )}
          </div>
          {obviousGroups.map((g) => (
            <GroupCard key={`${g.kind}-${g.key}`} g={g} canMerge={!!data?.can_merge} onMerged={refresh} />
          ))}
        </>
      )}

      {reviewGroups.length > 0 && (
        <>
          <h3 style={{ margin: '1rem 0 0.5rem' }}>На перевірку ({reviewGroups.length})</h3>
          {reviewGroups.map((g) => (
            <GroupCard key={`${g.kind}-${g.key}`} g={g} canMerge={!!data?.can_merge} onMerged={refresh} />
          ))}
        </>
      )}
    </div>
  );
}
